import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, Site } from '../lib/api';

const DEFAULT_SITE_NAME = (import.meta.env.VITE_SITE_NAME ?? 'Forum').trim();
const DEFAULT_SITE_DESCRIPTION = (import.meta.env.VITE_SITE_DESCRIPTION ?? '').trim();
const DEFAULT_SITE_URL = (import.meta.env.VITE_SITE_URL ?? '').trim();
const DEFAULT_SITE_IMAGE = (import.meta.env.VITE_SITE_IMAGE ?? '').trim();

interface SiteState {
  site: Site | null;
  loading: boolean;
  refresh: () => void;
}

const SiteContext = createContext<SiteState>({ site: null, loading: true, refresh: () => {} });

export function SiteProvider({ children }: { children: ReactNode }) {
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSite = () => {
    api.site
      .info()
      .then((res) => setSite(res.data))
      .catch(() => setSite(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSite(); }, []);

  // Dynamically update page title and meta tags from backend site info
  // with frontend env defaults as fallback.
  useEffect(() => {
    const meta = site?.meta_tags || {};
    const name = meta.og_title || site?.name || DEFAULT_SITE_NAME || 'Forum';
    const desc = meta.description || site?.description || DEFAULT_SITE_DESCRIPTION || '';
    const canonical = meta.canonical_url || site?.frontend_url || DEFAULT_SITE_URL || '';
    const image = meta.og_image || site?.logo_url || DEFAULT_SITE_IMAGE || '';

    document.title = name;
    const setMeta = (attr: string, key: string, value: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el && value) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      if (el) el.content = value;
    };
    setMeta('name', 'description', desc);
    if (meta.keywords) setMeta('name', 'keywords', meta.keywords);
    setMeta('property', 'og:site_name', site?.name || DEFAULT_SITE_NAME || '');
    setMeta('property', 'og:title', name);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:url', canonical);
    if (image) setMeta('property', 'og:image', image);
    setMeta('name', 'twitter:card', meta.twitter_card || 'summary');
    setMeta('name', 'twitter:title', name);
    setMeta('name', 'twitter:description', desc);
    if (image) setMeta('name', 'twitter:image', image);
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }
  }, [site]);

  return <SiteContext.Provider value={{ site, loading, refresh: fetchSite }}>{children}</SiteContext.Provider>;
}

export function useSite() {
  return useContext(SiteContext);
}
